
import React, { createContext, useContext } from "react";
import { useToast as useShadcnToast } from "@/hooks/use-toast";

type ToastContextType = {
  showSuccess: (message: string, description?: string) => void;
  showError: (message: string, description?: string) => void;
  showWarning: (message: string, description?: string) => void;
  showInfo: (message: string, description?: string) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toast } = useShadcnToast();

  const showSuccess = (message: string, description?: string) => {
    toast({
      title: message,
      description,
      variant: "default",
      className: "bg-green-50 border-green-200 text-green-800",
    });
  };

  const showError = (message: string, description?: string) => {
    toast({
      title: message,
      description,
      variant: "destructive",
    });
  };

  const showWarning = (message: string, description?: string) => {
    toast({
      title: message,
      description,
      variant: "default",
      className: "bg-amber-50 border-amber-200 text-amber-800",
    });
  };

  const showInfo = (message: string, description?: string) => {
    toast({
      title: message,
      description,
    });
  };

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showWarning, showInfo }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};
